import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getSubcategoryMaxParticipants, normalizeSubcategory } from '@/utils/eventUtils';

interface SubcategoryInfo {
  pairsCount: number;      // Parejas inscritas
  max: number | null;      // Capacidad máxima de parejas
  remaining: number | null; // Parejas disponibles
  isFull: boolean;         // ¿Está llena?
}

export function useAllSubcategoryCounts(
  eventId: string,
  category: string | null,
  subcategories: string[],
  eventRules: any
) {
  const [counts, setCounts] = useState<Record<string, SubcategoryInfo>>({});
  const [loading, setLoading] = useState(true);

  const fetchAllCounts = useCallback(async () => {
    if (!eventId || !category || subcategories.length === 0) {
      setCounts({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Una sola query para obtener TODOS los registros de esta categoría
      const { data, error } = await supabase
        .from('registrations')
        .select('subcategory')
        .eq('event_id', eventId)
        .ilike('category', category)
        .eq('status', 'confirmed');

      if (error) throw error;

      // Agrupar conteos por subcategoría NORMALIZADA (strip category prefix)
      const countsByCanonical: Record<string, number> = {};
      data?.forEach(reg => {
        const raw = reg.subcategory || '';
        const canonical = normalizeSubcategory(raw, category);
        countsByCanonical[canonical] = (countsByCanonical[canonical] || 0) + 1;
      });

      // Calcular info para cada subcategoría del dropdown
      const result: Record<string, SubcategoryInfo> = {};
      subcategories.forEach(subcategory => {
        // Normalize the dropdown option too for matching
        const canonical = normalizeSubcategory(subcategory, category);
        const registrationCount = countsByCanonical[canonical] || 0;
        const pairsCount = registrationCount;
        const max = getSubcategoryMaxParticipants(eventRules, category, subcategory);
        const remaining = max !== null ? Math.max(0, max - pairsCount) : null;
        
        result[subcategory] = {
          pairsCount,
          max,
          remaining,
          isFull: max !== null && pairsCount >= max
        };
      });

      setCounts(result);
    } catch (err) {
      console.error('Error fetching all subcategory counts:', err);
      setCounts({});
    } finally {
      setLoading(false);
    }
  }, [eventId, category, JSON.stringify(subcategories), eventRules]);

  useEffect(() => {
    fetchAllCounts();
  }, [fetchAllCounts]);

  return { counts, loading, refetch: fetchAllCounts };
}
